import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { OrderFormData } from "@/types/order";
import {
  useCreateOrderTemplate,
  useUpdateOrderTemplate,
  useOrderTemplates,
} from "@/hooks/use-order-templates";
import { OrderTemplate } from "@shared/schema";

interface SaveTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderData: OrderFormData;
  onSaveTemplate?: (template: OrderTemplate) => void;
  initialName?: string;
  initialDescription?: string;
  existingTemplateId?: string; // If provided, will update instead of create
}

export const SaveTemplateDialog: React.FC<SaveTemplateDialogProps> = ({
  open,
  onOpenChange,
  orderData,
  onSaveTemplate,
  initialName = "",
  initialDescription = "",
  existingTemplateId,
}) => {
  const { t } = useTranslation();
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [duplicateError, setDuplicateError] = useState(false);

  const createTemplate = useCreateOrderTemplate();
  const updateTemplate = useUpdateOrderTemplate();
  const { data: existingTemplates = [] } = useOrderTemplates();

  const isUpdateMode = !!existingTemplateId;

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setTemplateName(initialName || "");
      setTemplateDescription(initialDescription || orderData.description || "");
      setDuplicateError(false);
    }
  }, [open, initialName, initialDescription, orderData.description]);

  const handleSaveTemplate = async () => {
    const trimmedName = templateName.trim();

    // Prevent empty/whitespace-only names
    if (!trimmedName) {
      setDuplicateError(false);
      return;
    }

    // Check for duplicate template names (simple comparison, no trim needed)
    const duplicateTemplate = existingTemplates.find(
      (template) =>
        template.name === trimmedName && template.uid !== existingTemplateId,
    );

    if (duplicateTemplate) {
      setDuplicateError(true);
      return;
    }

    try {
      // Convert OrderFormData to template format (only include template-relevant fields)
      const templateProducts = orderData.products.map((p) => ({
        productId: p.uid,
        hajocaProductId: p.hajocaProductId,
        description: p.description,
        quantity: p.quantity,
        notes: "",
        // Note: No pricing (dynamic), no quantityReceived (fulfillment data)
      }));

      const templateData = {
        name: trimmedName,
        description: templateDescription.trim(),
        products: templateProducts,
      };

      let resultTemplate: OrderTemplate;

      if (isUpdateMode && existingTemplateId) {
        // Update existing template
        resultTemplate = await updateTemplate.mutateAsync({
          id: existingTemplateId,
          data: templateData,
        });
      } else {
        // Create new template
        resultTemplate = await createTemplate.mutateAsync(templateData);
      }

      // Pass the result back to the parent component
      onSaveTemplate?.(resultTemplate);
    } catch (error) {
      console.error(
        `Failed to ${isUpdateMode ? "update" : "create"} template:`,
        error,
      );
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  // Handle name change and clear duplicate error
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTemplateName(e.target.value);
    setDuplicateError(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] sm:max-w-[80vw] md:max-w-[60vw] lg:max-w-[500px] w-full mx-4">
        <DialogHeader>
          <DialogTitle>
            {isUpdateMode ? t("components.orders.saveTemplateDialog.updateOrderTemplate") : t("components.orders.saveTemplateDialog.saveAsOrderTemplate")}
          </DialogTitle>
          <DialogDescription>
            {isUpdateMode
              ? t("components.orders.saveTemplateDialog.updateThisTemplateWith")
              : t("components.orders.saveTemplateDialog.saveYourCurrentCart")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="template-name">{t("components.orders.saveTemplateDialog.templateName")}</Label>
            <Input
              id="template-name"
              value={templateName}
              onChange={handleNameChange}
              placeholder={t("components.orders.saveTemplateDialog.newOrderTemplate")}
              maxLength={255}
              className={
                duplicateError
                  ? "border-red-500 focus-visible:ring-red-500"
                  : ""
              }
            />
            {duplicateError && (
              <p className="text-sm text-red-600 mt-1">
                {t("components.orders.saveTemplateDialog.aTemplateWithThis")}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-description">{t("common.description")}</Label>
            <Textarea
              id="template-description"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder={t("components.orders.saveTemplateDialog.optionalDescriptionForThis")}
              rows={2}
              maxLength={1000}
              className="resize-none"
            />
          </div>

          <div className="text-sm text-muted-foreground">
            {orderData.products.length > 0 ? (
              <>
                {t("components.orders.saveTemplateDialog.thisTemplateWillInclude")} {orderData.products.length} product
                {orderData.products.length !== 1 ? "s" : ""} {t("components.orders.saveTemplateDialog.withTheirQuantities")}
              </>
            ) : (
              <div className="text-amber-600 bg-amber-50 p-2 rounded">
                {t("components.orders.saveTemplateDialog.noProductsSelectedAdd")}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="w-full sm:w-auto"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSaveTemplate}
            disabled={
              !templateName.trim() ||
              orderData.products.length === 0 ||
              createTemplate.isPending ||
              updateTemplate.isPending ||
              duplicateError
            }
            className="w-full sm:w-auto"
          >
            {createTemplate.isPending || updateTemplate.isPending
              ? `${isUpdateMode ? "Updating" : "Saving"}...`
              : isUpdateMode
                ? t("components.orders.saveTemplateDialog.updateTemplate")
                : t("components.orders.saveTemplateDialog.saveTemplate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
